import dayjs from 'dayjs';

import { DEFAULT_TIME_FORMAT, getDateFormat, getTimeFormat, TIME_FORMAT_PATTERNS } from './dateDisplay';

/**
 * The date grammar people type.
 *
 * `DateField` is keyboard-first: the calendar is the second door, not the
 * way in. This is the pure parser behind it — text in, the STORED shape and
 * a human confirmation out — shared with the filter builder's value box so
 * "+7d" means the same thing in a field and in a filter.
 *
 * A library was considered (chrono-node) and rejected: ~100 KB minified,
 * locale-heavy, and it does not know the `+7d` grammar the platform already
 * speaks in automation tokens (`{{today+7d}}`) and KQL (`-7d`, `-3mo`).
 * Managers should learn one grammar, not three.
 *
 * WHAT IT TAKES
 *
 *   Named days      today · tomorrow · yesterday · now
 *   Weekdays        fri · friday · next fri · last mon · this fri
 *                   A bare weekday is the NEXT occurrence, TODAY IF TODAY
 *                   (you do not type "fri" on a Friday to mean next week);
 *                   `next fri` is strictly after today, `last fri` strictly
 *                   before it.
 *   Offsets         +3d · -2w · +1mo · +1m · +1y (also +3 days, -2 weeks …)
 *                   The automation-token units: d day, w week, m/mo month,
 *                   y year.
 *   Absolute        2026-09-14 · 9/14 · 9/14/26 · 9/14/2026 · sep 14 ·
 *                   14 sep · sep 14 2026 · 14-sep-2026 · september 14, 2026
 *   Times           3pm · 3 pm · 3:15p · 15:00 · noon · midnight, plus the
 *                   military 1500 / 930 where a date part or `time` mode
 *                   removes the ambiguity with a year.
 *   Combinations    tomorrow 3pm · fri 9am · sep 14 15:00 · 9/14 3pm ·
 *                   "at" and "@" are noise words ("tomorrow at 3pm").
 *
 * AMBIGUITY. `9/14` versus `14/9` is settled by the user's `dateFormat`
 * preference: a pattern that starts with D reads day first. Either way, a
 * first number that cannot be a month and a second one that can resolves the
 * other way round, because the reading that produces a real date is the one
 * the person meant.
 *
 * A YEAR that is not typed is the current one — predictable beats clever, and
 * the confirmation text spells the year out whenever it is not this one. A
 * two-digit year follows the POSIX pivot: 00-68 → 2000s, 69-99 → 1900s.
 *
 * MODES. `date` ignores a time part ("sep 14 3pm" is Sep 14). `time` refuses
 * a date part. `date-time` puts a date with no time at midnight, EXCEPT the
 * two forms that name an instant rather than a day — `now`, and the offsets,
 * which keep the current clock time ("+3d" at 2:40pm is 2:40pm in three days).
 */

const WEEKDAY_ALIASES = {
	sun: 0, sunday: 0,
	mon: 1, monday: 1,
	tue: 2, tues: 2, tuesday: 2,
	wed: 3, weds: 3, wednesday: 3,
	thu: 4, thur: 4, thurs: 4, thursday: 4,
	fri: 5, friday: 5,
	sat: 6, saturday: 6,
};

const MONTH_NAMES = [
	'january', 'february', 'march', 'april', 'may', 'june',
	'july', 'august', 'september', 'october', 'november', 'december',
];

const MONTH_ALIASES = MONTH_NAMES.reduce((map, name, index) => {
	map[name] = index;
	map[name.slice(0, 3)] = index;
	return map;
}, { sept: 8 });

const OFFSET_UNITS = {
	d: 'day', day: 'day', days: 'day',
	w: 'week', week: 'week', weeks: 'week',
	m: 'month', mo: 'month', mos: 'month', mon: 'month', month: 'month', months: 'month',
	y: 'year', yr: 'year', yrs: 'year', year: 'year', years: 'year',
};

const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const NUMERIC_RE = /^(\d{1,2})[/\-.](\d{1,2})(?:[/\-.](\d{2}|\d{4}))?$/;
// The separator between a month NAME and the numbers around it is a space, a
// dash or a dot, because the `DD-MMM-YYYY` date-format preference prints
// "14-Sep-2026" and a field must be able to read back what it just printed.
// (Purely numeric forms stay with NUMERIC_RE above; a letter run is what
// tells these two apart from it.)
const MONTH_DAY_RE = /^([a-z]{3,9})\.?[ \-.](\d{1,2})(?:[ \-.](\d{2}|\d{4}))?$/;
const DAY_MONTH_RE = /^(\d{1,2})[ \-.]([a-z]{3,9})\.?(?:[ \-.](\d{2}|\d{4}))?$/;
const OFFSET_RE = /^([+-]) ?(\d{1,4}) ?([a-z]{1,6})$/;
const WEEKDAY_RE = /^(?:(next|last|this) )?([a-z]{3,9})$/;
const MERIDIEM_TIME_RE = /^(\d{1,2})(?::(\d{2}))? ?([ap])m?$/;
const CLOCK_TIME_RE = /^(\d{1,2}):(\d{2})$/;
const MILITARY_TIME_RE = /^(\d{3,4})$/;

/** A date-format preference that starts with D is read day-first (D/M/Y). */
export function isDayFirstFormat(format) {
	return /^d/i.test(String(format || '').trim());
}

/** POSIX pivot: 00-68 are this century, 69-99 the last one. */
const expandYear = (digits) => {
	const year = Number(digits);
	if (digits.length === 4) return year;
	return year <= 68 ? 2000 + year : 1900 + year;
};

/**
 * A real calendar day or nothing. Built through the Date constructor and then
 * read back, because both `new Date(y, m, d)` and dayjs's own string parser
 * silently ROLL a bad day over (Feb 30 → Mar 2) instead of refusing it.
 */
const makeDate = (year, monthIndex, day) => {
	if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
	const built = dayjs(new Date(year, monthIndex, day, 0, 0, 0, 0));
	if (!built.isValid()) return null;
	if (built.year() !== year || built.month() !== monthIndex || built.date() !== day) return null;
	return built;
};

/**
 * A clock time, or null. `allowMilitary` gates the bare 1500 / 930 form,
 * which is only unambiguous where a year cannot be meant — `time` mode, or
 * the tail of a string whose head already parsed as a date.
 */
function parseTimeText(text, { allowMilitary = false } = {}) {
	const value = String(text || '').trim().toLowerCase().replace(/\./g, '');
	if (!value) return null;

	if (value === 'noon' || value === 'midday') return { hour: 12, minute: 0 };
	if (value === 'midnight') return { hour: 0, minute: 0 };

	let match = MERIDIEM_TIME_RE.exec(value);
	if (match) {
		let hour = Number(match[1]);
		const minute = match[2] ? Number(match[2]) : 0;
		if (hour < 1 || hour > 12 || minute > 59) return null;
		if (match[3] === 'a') hour = hour === 12 ? 0 : hour;
		else hour = hour === 12 ? 12 : hour + 12;
		return { hour, minute };
	}

	match = CLOCK_TIME_RE.exec(value);
	if (match) {
		const hour = Number(match[1]);
		const minute = Number(match[2]);
		if (hour > 23 || minute > 59) return null;
		return { hour, minute };
	}

	if (allowMilitary) {
		match = MILITARY_TIME_RE.exec(value);
		if (match) {
			const digits = match[1].padStart(4, '0');
			const hour = Number(digits.slice(0, 2));
			const minute = Number(digits.slice(2));
			if (hour > 23 || minute > 59) return null;
			return { hour, minute };
		}
	}

	return null;
}

/**
 * A date expression → `{ date (local midnight), kind, clockFromNow }`.
 * `clockFromNow` marks the forms that name an instant rather than a day, so
 * a date-time field keeps the current clock instead of dropping to midnight.
 */
function parseDateText(text, { now, dayFirst }) {
	const value = String(text || '').trim().toLowerCase();
	if (!value) return null;
	const base = now.startOf('day');

	if (value === 'today' || value === 'tod') return { date: base, kind: 'today' };
	if (value === 'tomorrow' || value === 'tmw' || value === 'tmrw') return { date: base.add(1, 'day'), kind: 'tomorrow' };
	if (value === 'yesterday' || value === 'yest') return { date: base.subtract(1, 'day'), kind: 'yesterday' };

	let match = OFFSET_RE.exec(value);
	if (match) {
		const unit = OFFSET_UNITS[match[3]];
		if (unit) {
			const amount = Number(match[2]) * (match[1] === '-' ? -1 : 1);
			return { date: base.add(amount, unit), kind: 'relative', clockFromNow: true };
		}
	}

	match = WEEKDAY_RE.exec(value);
	if (match) {
		const target = WEEKDAY_ALIASES[match[2]];
		if (target !== undefined) {
			const current = base.day();
			let delta;
			if (match[1] === 'last') delta = -(((current - target + 7) % 7) || 7);
			else if (match[1] === 'next') delta = ((target - current + 7) % 7) || 7;
			// Bare (and "this"): the next occurrence, today included.
			else delta = (target - current + 7) % 7;
			return { date: base.add(delta, 'day'), kind: 'weekday' };
		}
	}

	match = ISO_RE.exec(value);
	if (match) {
		const built = makeDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
		return built ? { date: built, kind: 'iso' } : null;
	}

	match = NUMERIC_RE.exec(value);
	if (match) {
		const first = Number(match[1]);
		const second = Number(match[2]);
		let month = dayFirst ? second : first;
		let day = dayFirst ? first : second;
		// The reading that yields a real month is the one that was meant.
		if (month > 12 && day <= 12) {
			const swap = month;
			month = day;
			day = swap;
		}
		const year = match[3] ? expandYear(match[3]) : now.year();
		const built = makeDate(year, month - 1, day);
		return built ? { date: built, kind: 'numeric' } : null;
	}

	match = MONTH_DAY_RE.exec(value);
	if (match && MONTH_ALIASES[match[1]] !== undefined) {
		const year = match[3] ? expandYear(match[3]) : now.year();
		const built = makeDate(year, MONTH_ALIASES[match[1]], Number(match[2]));
		return built ? { date: built, kind: 'month-name' } : null;
	}

	match = DAY_MONTH_RE.exec(value);
	if (match && MONTH_ALIASES[match[2]] !== undefined) {
		const year = match[3] ? expandYear(match[3]) : now.year();
		const built = makeDate(year, MONTH_ALIASES[match[2]], Number(match[1]));
		return built ? { date: built, kind: 'month-name' } : null;
	}

	return null;
}

/* ---------------------------------------------------------------------------
 * Coarse entry: `precision: 'month' | 'year'`
 *
 * A month-precision element ("model year", "expires") is still STORED as a
 * whole day — the first of the month, Jan 1 of the year — so nothing about
 * sorting or KQL changes; what changes is what may be typed and what comes
 * back. These forms are the ones the day grammar above cannot read, because
 * they have no day in them: "sep 2026", "2026-09", "9/2026", "2026".
 *
 * The numeric forms insist on a FOUR-digit year, so "9/14" is still Sep 14
 * (snapped to its month below) rather than September 2014 — a person typing
 * a month and a two-digit number almost always means a day.
 *
 * A YEAR field reads the month forms too and simply keeps their year: it
 * already takes "sep 14 2028" for 2028, so "sep 2026" — which says less and
 * names the same year — cannot be the one spelling it refuses.
 * ------------------------------------------------------------------------- */

const COARSE_YEAR_RE = /^(\d{4})$/;
const COARSE_YEAR_MONTH_RE = /^(\d{4})[-/.](\d{1,2})$/;
const COARSE_MONTH_YEAR_RE = /^(\d{1,2})[-/.](\d{4})$/;
const COARSE_MONTH_NAME_RE = /^([a-z]{3,9})\.?(?:[ \-.](\d{2}|\d{4}))?$/;

/** A month/year expression → a dayjs at the first day it names, or null. */
function parseCoarseText(text, { now }) {
	const value = String(text || '').trim().toLowerCase();
	if (!value) return null;

	let match = COARSE_YEAR_RE.exec(value);
	if (match) return makeDate(Number(match[1]), 0, 1);

	// The month forms are read at EITHER grain. A year field snaps them the
	// way it snaps everything else the grammar understands — it already takes
	// "sep 14 2028" for 2028, so refusing "sep 2026", which says less and
	// names the same year, would be a red field for a value the reader spelled
	// out. (`snapToPrecision` is what makes the month it named irrelevant.)
	match = COARSE_YEAR_MONTH_RE.exec(value);
	if (match) return makeDate(Number(match[1]), Number(match[2]) - 1, 1);

	match = COARSE_MONTH_YEAR_RE.exec(value);
	if (match) return makeDate(Number(match[2]), Number(match[1]) - 1, 1);

	match = COARSE_MONTH_NAME_RE.exec(value);
	if (match && MONTH_ALIASES[match[1]] !== undefined) {
		const year = match[2] ? expandYear(match[2]) : now.year();
		return makeDate(year, MONTH_ALIASES[match[1]], 1);
	}
	return null;
}

/** The first day of the span a precision reads, as the stored day. */
const snapToPrecision = (moment, precision) => (
	precision === 'year' ? moment.startOf('year') : moment.startOf('month')
);

/** "September 2026" / "2026" — what a coarse field confirms back. */
const confirmCoarse = (moment, precision) => (
	precision === 'year' ? moment.format('YYYY') : moment.format('MMMM YYYY')
);

/** "Mon, Sep 14" — with the year whenever it is not the year we are in. */
const confirmDay = (moment, now) =>
	moment.year() === now.year()
		? moment.format('ddd, MMM D')
		: moment.format('ddd, MMM D, YYYY');

const clockPatternFor = (preference) =>
	TIME_FORMAT_PATTERNS[preference] || TIME_FORMAT_PATTERNS[DEFAULT_TIME_FORMAT];

/**
 * Parse typed date text.
 *
 * @param {string} text
 * @param {object} [options]
 * @param {'date'|'date-time'|'time'} [options.mode='date']
 * @param {string} [options.dateFormat] a dayjs pattern; the preference by default
 * @param {'12h'|'24h'} [options.timeFormat] the preference by default
 * @param {*} [options.now] anything dayjs reads; the current instant by default
 * @param {'day'|'month'|'year'} [options.precision='day'] a `date` element's
 *   reading grain. 'month' / 'year' add the coarse forms ("sep 2026", "2026")
 *   and snap every other reading to the first day of the span it names — the
 *   stored value is a whole day either way.
 * @returns {{ value: string, confirm: string, kind: string }|null}
 *   `value` is the STORED shape for the mode — 'YYYY-MM-DD' for `date`,
 *   'HH:mm' for `time`, an ISO timestamp for `date-time`. `confirm` is the
 *   reading in words, for the field's passive suffix. `kind` names the
 *   grammar that matched. Null means "not a date" — the caller keeps what it
 *   had and flags the text, it never clears the field.
 */
export function parseDateInput(text, { mode = 'date', dateFormat, timeFormat, now, precision = 'day' } = {}) {
	if (typeof text !== 'string') return null;
	const trimmed = text.trim();
	if (!trimmed) return null;

	const nowMoment = now ? dayjs(now) : dayjs();
	if (!nowMoment.isValid()) return null;

	const dayFirst = isDayFirstFormat(dateFormat === undefined ? getDateFormat() : dateFormat);
	const clock = clockPatternFor(timeFormat === undefined ? getTimeFormat() : timeFormat);

	const normalized = trimmed
		.toLowerCase()
		.replace(/,/g, ' ')
		.replace(/\s+/g, ' ')
		.replace(/ (?:at|@) /g, ' ')
		.trim();

	// A coarse `date` element reads its own forms first, then falls through to
	// the day grammar and snaps whatever it understood ("today" in a month
	// field is this month). Only a `date` has a precision — a clock has no
	// month to be coarse about.
	const coarse = (mode === 'date' && (precision === 'month' || precision === 'year')) ? precision : null;
	if (coarse) {
		const direct = parseCoarseText(normalized, { now: nowMoment });
		if (direct) {
			const snapped = snapToPrecision(direct, coarse);
			return { value: snapped.format('YYYY-MM-DD'), confirm: confirmCoarse(snapped, coarse), kind: coarse };
		}
		const fine = normalized === 'now'
			? { date: nowMoment.startOf('day'), kind: 'now' }
			: parseDateText(normalized, { now: nowMoment, dayFirst });
		if (!fine) return null;
		const snapped = snapToPrecision(fine.date, coarse);
		return { value: snapped.format('YYYY-MM-DD'), confirm: confirmCoarse(snapped, coarse), kind: fine.kind };
	}

	if (normalized === 'now') {
		if (mode === 'time') {
			return { value: nowMoment.format('HH:mm'), confirm: nowMoment.format(clock), kind: 'now' };
		}
		if (mode === 'date') {
			return { value: nowMoment.format('YYYY-MM-DD'), confirm: confirmDay(nowMoment, nowMoment), kind: 'now' };
		}
		// The whole instant: "now" means now, seconds included, so two
		// readings taken in the same minute do not become the same reading.
		return {
			value: nowMoment.toISOString(),
			confirm: `${confirmDay(nowMoment, nowMoment)} · ${nowMoment.format(clock)}`,
			kind: 'now',
		};
	}

	if (mode === 'time') {
		const parsed = parseTimeText(normalized, { allowMilitary: true });
		if (!parsed) return null;
		const moment = nowMoment.hour(parsed.hour).minute(parsed.minute).second(0).millisecond(0);
		return { value: moment.format('HH:mm'), confirm: moment.format(clock), kind: 'time' };
	}

	const tokens = normalized.split(' ');
	let found = null;

	// The whole string as a date FIRST, so "sep 14 2026" is a year and not
	// the military time 20:26.
	const whole = parseDateText(normalized, { now: nowMoment, dayFirst });
	if (whole) found = { ...whole, time: null };

	// Otherwise a date followed by a time, shortest time tail first ("3 pm"
	// only parses once "pm" has failed).
	for (let split = tokens.length - 1; split >= 1 && !found; split -= 1) {
		const time = parseTimeText(tokens.slice(split).join(' '), { allowMilitary: true });
		if (!time) continue;
		const date = parseDateText(tokens.slice(0, split).join(' '), { now: nowMoment, dayFirst });
		if (!date) continue;
		found = { ...date, time };
	}

	// A bare time in a date-time field means today at that time. A `date`
	// field has nothing to take from it, so it refuses instead of guessing.
	if (!found && mode === 'date-time') {
		const time = parseTimeText(normalized, { allowMilitary: false });
		if (time) found = { date: nowMoment.startOf('day'), kind: 'time', time };
	}

	if (!found) return null;

	if (mode === 'date') {
		return { value: found.date.format('YYYY-MM-DD'), confirm: confirmDay(found.date, nowMoment), kind: found.kind };
	}

	// A typed clock lands on :00 because that is what was typed; the forms
	// that name an INSTANT ("+3d", and `now` above) carry the current clock
	// across whole, seconds included; a bare day sits at local midnight.
	let moment;
	if (found.time) {
		moment = found.date.hour(found.time.hour).minute(found.time.minute).second(0).millisecond(0);
	}
	else if (found.clockFromNow) {
		moment = found.date
			.hour(nowMoment.hour()).minute(nowMoment.minute())
			.second(nowMoment.second()).millisecond(nowMoment.millisecond());
	}
	else moment = found.date;

	return {
		value: moment.toISOString(),
		confirm: `${confirmDay(moment, nowMoment)} · ${moment.format(clock)}`,
		kind: found.kind,
	};
}

export default parseDateInput;
